import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  alpha,
  Alert,
} from "@mui/material";
import { styleForTextField } from "../../../../../../utils/TextField/TextField";
import CustomDatePicker from "../../../../../../utils/DatePicker/CustomDatePicker";
import {
  formatMoney,
  fromDateString,
  parseAmount,
  toDateString,
} from "../utils/calculator";

const amountFields = new Set([
  "cost",
  "deposit",
  "recurringAmount",
  "finalAmount",
]);

const ContractTermsForm = ({
  form,
  errors,
  onChange,
  onDateChange,
  onSubmit,
}) => {
  const financed = useMemo(() => {
    const cost = parseAmount(form.cost);
    const deposit = parseAmount(form.deposit);
    if (!Number.isFinite(cost - deposit)) return 0;
    return Math.max(0, cost - deposit);
  }, [form.cost, form.deposit]);

  const handleKeyDown = (e) => {
    if (e.key === "e" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01),
      }}
    >
      <Stack spacing={3}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='flex-start'
          gap={2}
          flexWrap='wrap'
        >
          <Box>
            <Typography
              variant='h6'
              fontWeight={600}
              color='primary.main'
              gutterBottom
            >
              Contract Terms
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Enter irregular payment terms. All amounts are in dollars ($).
            </Typography>
          </Box>
          <Box textAlign='right'>
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={700}
              letterSpacing={0.8}
            >
              AMOUNT FINANCED
            </Typography>
            <Typography variant='h6' fontWeight={700} color='primary.main'>
              $ {formatMoney(financed)}
            </Typography>
          </Box>
        </Box>

        <Box component='form' onSubmit={onSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label='Cost of asset'
                name='cost'
                value={form.cost}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                required
                fullWidth
                autoComplete='off'
                error={Boolean(errors.cost)}
                helperText={errors.cost || "Cost of asset, excluding interest"}
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                sx={styleForTextField}
              />
              <TextField
                label='Deposit paid'
                name='deposit'
                value={form.deposit}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                fullWidth
                autoComplete='off'
                placeholder='0.00'
                error={Boolean(errors.deposit)}
                helperText={errors.deposit || "Optional deposit paid upfront"}
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                sx={styleForTextField}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label='Recurring instalments'
                name='recurringCount'
                type='number'
                value={form.recurringCount}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                required
                fullWidth
                autoComplete='off'
                error={Boolean(errors.recurringCount)}
                helperText={
                  errors.recurringCount ||
                  "Number of equal recurring instalments before the final payment"
                }
                InputProps={{
                  inputProps: { min: 1, step: 1, inputMode: "numeric" },
                }}
                sx={styleForTextField}
              />
              <TextField
                label='Monthly instalment'
                name='recurringAmount'
                value={form.recurringAmount}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                required
                fullWidth
                autoComplete='off'
                error={Boolean(errors.recurringAmount)}
                helperText={
                  errors.recurringAmount ||
                  "Amount for each recurring instalment"
                }
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                sx={styleForTextField}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label='Final payment'
                name='finalAmount'
                value={form.finalAmount}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                required
                fullWidth
                autoComplete='off'
                error={Boolean(errors.finalAmount)}
                helperText={
                  errors.finalAmount || "Final (balloon / last) payment amount"
                }
                InputProps={{
                  startAdornment: (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      $
                    </Typography>
                  ),
                  inputProps: { inputMode: "decimal" },
                }}
                sx={styleForTextField}
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <CustomDatePicker
                label='First instalment date'
                selected={fromDateString(form.firstDate)}
                onChange={(date) => onDateChange(toDateString(date))}
                dateFormat='dd-MM-yyyy'
                fullWidth
                error={Boolean(errors.firstDate)}
                helperText={
                  errors.firstDate || "Date when the first instalment is due"
                }
              />
            </Stack>

            <Alert severity='info' sx={{ borderRadius: 2 }}>
              Payments are scheduled monthly. Dates adjust automatically for
              shorter months. You can edit individual payment amounts after
              generating the schedule.
            </Alert>

            <Stack direction='row' spacing={2}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                sx={{ minWidth: 200 }}
              >
                Generate Schedule
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

export { amountFields };
export default ContractTermsForm;
